import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useNavigate, type LoaderFunctionArgs } from 'react-router-dom';
import { cn, Table, TableBody, TableCell, TableHeader, TableRow } from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import {
  ApiCallCredentialClassSchema,
  ApiKeyRequestOutcomeSchema,
  type ApiCallCredentialClass,
  type ApiCallListItem,
  type ApiKeyRequestOutcomeDto,
  type ListApiCallsQuery,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { resolvedPath } from '@/lib/api-calls/resolved-path';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import {
  ClockIcon,
  KeyIcon,
  MailIcon,
  ShieldIcon,
  TagIcon,
  TerminalIcon,
  TextIcon,
  WorkspacesIcon,
} from '@/components/layout/icons';
import { FilterBar, FilterPill } from '@/components/filter-bar';
import { CommitOnBlurInput } from '@/components/commit-on-blur-input';
import { ListEmptyState, TestWorkspacesPill, WorkspaceLabel } from '@/components/test-workspaces';
import { readDateParam, readEnumParam, readUuidParam, useListParams } from '@/lib/list-view/use-list-params';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';
import { includeTestWorkspacesQuery } from '@/lib/test-workspaces/include-test-workspaces';

// The `auth-failed` pill LIVES here (unlike the per-key trail, where it can never
// match). This is the surface that closes IMM-441 / IMM-446.
const OUTCOMES: ReadonlyArray<ApiKeyRequestOutcomeDto> = [
  'success',
  'denied',
  'rate-limited',
  'client-error',
  'server-error',
  'auth-failed',
];

// Hand-picked rather than derived from the schema enum: the pills are the
// classes an operator actually filters by. `workspace-mcp` (IMM-601.6) earns one
// because customer MCP tool traffic is exactly what an incident needs isolated
// from plain HTTP API traffic, and `workspace-mcp-oauth` (IMM-601.8.1) gets its
// own beside it: a connected third-party client is the credential an operator
// most needs to isolate on its own, not folded in with the workspace's own keys.
const CREDENTIAL_CLASSES: ReadonlyArray<ApiCallCredentialClass> = [
  'api-key',
  'workspace-mcp',
  'workspace-mcp-oauth',
  'member-session',
  'operator-session',
  'impersonation',
  'anonymous',
  'malformed',
];

// The verbs worth a one-click pill. `OPTIONS` earns its place: browser CORS
// preflights are a large share of what the store captures, so being able to
// exclude them (or look only at them) is the point of the filter. The API
// itself accepts ANY verb string, so a captured method with no pill here is
// still filterable by hand via `?method=`.
const METHODS: ReadonlyArray<string> = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type LoaderData = {
  readonly calls: ReadonlyArray<ApiCallListItem>;
  readonly nextCursor: string | null;
  readonly columnPreference: ColumnPreference;
};

// `datetime-local` yields `YYYY-MM-DDTHH:mm`; convert to a full ISO string for
// the contract's `z.iso.datetime()`. Empty / unparseable → null (param removed).
function toIso(localValue: string): string | null {
  if (localValue === '') return null;
  const date = new Date(localValue);

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function isoToLocalInput(iso: string | null): string {
  if (iso === null) return '';
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// The structured filters, read from the URL search params so the loader and the
// "load more" fetch agree. Only well-formed values reach the backend: the read
// side validates, so a hand-edited value is ignored rather than forwarded.
// The test-workspace flag is NOT read from the URL: it is the shared,
// browser-persisted filter (IMM-593.2), spread in at each call site from
// `includeTestWorkspacesQuery()`.
type ListApiCallsFilters = Omit<ListApiCallsQuery, 'includeTestWorkspaces'>;

function queryFromParams(searchParams: URLSearchParams): ListApiCallsFilters {
  const query: ListApiCallsFilters = {};
  const workspaceId = readUuidParam(searchParams, 'workspaceId');
  const apiKeyId = readUuidParam(searchParams, 'apiKeyId');
  const credentialClass = readEnumParam(searchParams, 'credentialClass', ApiCallCredentialClassSchema.options);
  const outcome = readEnumParam(searchParams, 'outcome', ApiKeyRequestOutcomeSchema.options);
  const method = searchParams.get('method');
  const endpoint = searchParams.get('endpoint');
  const from = readDateParam(searchParams, 'from');
  const to = readDateParam(searchParams, 'to');

  if (workspaceId !== null) query.workspaceId = workspaceId;

  if (apiKeyId !== null) query.apiKeyId = apiKeyId;

  if (credentialClass !== null) query.credentialClass = credentialClass;

  if (outcome !== null) query.outcome = outcome;

  if (method) query.method = method;

  if (endpoint) query.endpoint = endpoint;

  // The contract wants a full ISO datetime; a parseable hand-edited date is normalised here, never in the address.
  if (from !== null) query.from = new Date(from).toISOString();

  if (to !== null) query.to = new Date(to).toISOString();

  return query;
}

export const apiCallsLoaderParams = [
  'workspaceId',
  'apiKeyId',
  'credentialClass',
  'outcome',
  'method',
  'endpoint',
  'from',
  'to',
] as const;

export async function apiCallsLoader({ request }: LoaderFunctionArgs): Promise<LoaderData> {
  const query = queryFromParams(new URL(request.url).searchParams);

  const [result, columnPreference] = await Promise.all([
    apiClient.adminApiCalls.list({ query: { ...query, ...includeTestWorkspacesQuery() } }),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.apiCalls),
  ]);

  if (!result.ok) {
    throw new Response('Failed to load API calls', { status: result.status });
  }

  return { calls: result.body.data, nextCursor: result.body.nextCursor, columnPreference };
}

// The caller identity captured at request time: the key label, the member email,
// or the acting operator — whichever the credential class resolved. `null` when
// none did (anonymous / malformed); the cell shows the placeholder and the sort
// groups it last.
function originLabel(row: ApiCallListItem): string | null {
  return (
    row.apiKeyName ?? row.memberEmail ?? row.operatingAdminEmail ?? row.operatingAdminId ?? row.apiKeyId ?? row.memberId
  );
}

function labelFor(name: string | null, id: string | null): string | null {
  if (name !== null && name !== '') return name;

  if (id !== null && id !== '') return id;

  return null;
}

// The width-less columns (Workspace, Herkunft, Endpunkt) grow with their data: the icon heads overflow
// `main` by 364px at 1280px and still by 204px at 1440px, and only fit from ~1650px, so labels show
// from 1920px and the head's `title` carries them below. Written as a whole literal so Tailwind emits
// it (BL-034, BL-065; IMM-1074.7 plan).
const LABEL_FROM_1920 = 'hidden min-[1920px]:inline';

function useApiCallColumns(): ReadonlyArray<OperatorColumn<ApiCallListItem>> {
  const t = useTranslations('pages.admin.api-calls');
  const intl = useIntl();

  return useMemo(() => {
    const placeholder = t('placeholder');

    return [
      {
        id: 'occurredAt',
        label: t('columns.occurred-at'),
        icon: ClockIcon,
        headClassName: 'w-[150px]',
        headLabelClassName: LABEL_FROM_1920,
        cellClassName: 'text-text-tertiary tabular-nums',
        cell: (row) => intl.formatDate(row.occurredAt, { dateStyle: 'short', timeStyle: 'short' }),
        sortValue: (row) => Date.parse(row.occurredAt),
      },
      {
        id: 'workspace',
        label: t('columns.workspace'),
        icon: WorkspacesIcon,
        headLabelClassName: LABEL_FROM_1920,
        cell: (row) => (
          <WorkspaceLabel isTest={row.isTest}>
            {labelFor(row.workspaceName, row.workspaceId) ?? placeholder}
          </WorkspaceLabel>
        ),
        sortValue: (row) => labelFor(row.workspaceName, row.workspaceId),
      },
      {
        id: 'credentialClass',
        label: t('columns.credential-class'),
        icon: ShieldIcon,
        headClassName: 'w-[160px]',
        headLabelClassName: LABEL_FROM_1920,
        cell: (row) => t(`credential-classes.${row.credentialClass}`),
        sortValue: (row) => t(`credential-classes.${row.credentialClass}`),
      },
      {
        id: 'origin',
        label: t('columns.origin'),
        icon: MailIcon,
        headLabelClassName: LABEL_FROM_1920,
        cellClassName: 'text-sm',
        cell: (row) => originLabel(row) ?? placeholder,
        sortValue: originLabel,
      },
      {
        id: 'endpoint',
        label: t('columns.endpoint'),
        icon: TextIcon,
        headLabelClassName: LABEL_FROM_1920,
        cellClassName: 'font-mono text-sm',
        // The resolved path, not the bare template — otherwise every
        // row on a busy endpoint is the same string. The template
        // itself is still on the detail page, and is what the
        // endpoint filter matches.
        cell: (row) => (
          <>
            <span className="text-text-tertiary">{row.method}</span> {resolvedPath(row.endpoint, row.pathParams)}
          </>
        ),
        sortValue: (row) => `${row.method} ${resolvedPath(row.endpoint, row.pathParams)}`,
      },
      {
        id: 'mcpMethod',
        label: t('columns.mcp-method'),
        icon: TerminalIcon,
        headClassName: 'w-[150px]',
        headLabelClassName: LABEL_FROM_1920,
        cellClassName: 'font-mono text-sm',
        cell: (row) => row.mcpMethod ?? placeholder,
        sortValue: (row) => row.mcpMethod,
      },
      {
        id: 'status',
        label: t('columns.status'),
        icon: TagIcon,
        headClassName: 'w-[80px]',
        headLabelClassName: LABEL_FROM_1920,
        cellClassName: 'tabular-nums',
        cell: (row) => row.statusCode,
        sortValue: (row) => row.statusCode,
      },
      {
        id: 'outcome',
        label: t('columns.outcome'),
        icon: KeyIcon,
        headClassName: 'w-[190px]',
        headLabelClassName: LABEL_FROM_1920,
        cell: (row) => t(`outcomes.${row.outcome}`),
        sortValue: (row) => t(`outcomes.${row.outcome}`),
      },
    ];
  }, [t, intl]);
}

export function ApiCallsPage() {
  const t = useTranslations('pages.admin.api-calls');
  const navigate = useNavigate();
  const { calls, nextCursor, columnPreference } = useLoaderData() as LoaderData;
  const { params: searchParams, setParams } = useListParams();

  // Accumulate keyset pages client-side: the loader gives page 1 (and resets the
  // list whenever the filters change), "Mehr laden" appends the next page.
  const [rows, setRows] = useState<ReadonlyArray<ApiCallListItem>>(calls);
  const [cursor, setCursor] = useState<string | null>(nextCursor);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setRows(calls);
    setCursor(nextCursor);
  }, [calls, nextCursor]);

  const catalogue = useApiCallColumns();
  const table = useOperatorTable({
    rows,
    preferenceKey: OPERATOR_COLUMN_KEYS.apiCalls,
    catalogue,
    alwaysVisibleId: 'occurredAt',
    loadedPreference: columnPreference,
  });

  const selectedOutcome = readEnumParam(searchParams, 'outcome', ApiKeyRequestOutcomeSchema.options);
  const selectedCredentialClass = readEnumParam(searchParams, 'credentialClass', ApiCallCredentialClassSchema.options);
  const selectedMethod = searchParams.get('method');

  function toggleOutcome(outcome: ApiKeyRequestOutcomeDto): void {
    setParams({ outcome: selectedOutcome === outcome ? null : outcome });
  }

  function toggleCredentialClass(credentialClass: ApiCallCredentialClass): void {
    setParams({ credentialClass: selectedCredentialClass === credentialClass ? null : credentialClass });
  }

  // No pill active = no `method` param = every verb, the default.
  function toggleMethod(method: string): void {
    setParams({ method: selectedMethod === method ? null : method });
  }

  // The id filters are committed on blur / Enter and only when well-formed — an
  // invalid value clears the param rather than 400-ing the loader.
  function commitUuid(key: 'workspaceId' | 'apiKeyId', value: string): void {
    const trimmed = value.trim();
    setParams({ [key]: trimmed !== '' && UUID_RE.test(trimmed) ? trimmed : null });
  }

  async function loadMore(): Promise<void> {
    if (cursor === null || loadingMore) return;
    setLoadingMore(true);

    try {
      const result = await apiClient.adminApiCalls.list({
        query: { ...queryFromParams(searchParams), ...includeTestWorkspacesQuery(), cursor },
      });

      if (result.ok) {
        setRows((prev) => [...prev, ...result.body.data]);
        setCursor(result.body.nextCursor);
      }
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <>
      <PageTopbar leading={<ListLeading title={t('title')} count={rows.length} icon={ClockIcon} />} />
      <div className="space-y-1 border-b border-border bg-surface px-6 py-3">
        <p className="text-sm text-text-secondary">{t('subtitle')}</p>
        <p className="text-sm text-text-tertiary">{t('temporary-notice')}</p>
      </div>
      <FilterBar
        pills={
          <>
            {OUTCOMES.map((outcome) => (
              <FilterPill
                key={outcome}
                icon={TagIcon}
                label={t(`outcomes.${outcome}`)}
                active={selectedOutcome === outcome}
                onToggle={() => toggleOutcome(outcome)}
              />
            ))}
            <TestWorkspacesPill />
          </>
        }
        searchValue={searchParams.get('endpoint') ?? ''}
        searchPlaceholder={t('filters.endpoint-placeholder')}
        onSearchChange={(next, commit) => setParams({ endpoint: next }, { replace: commit.continuing })}
        trailing={<ColumnsTrigger {...table.columns.trigger} />}
      />
      <div className="flex flex-wrap items-end gap-3 border-b border-border bg-surface px-4 py-2">
        <div className="flex flex-col gap-1 text-sm text-text-tertiary">
          {t('filters.credential-class')}
          <div className="flex flex-wrap gap-1.5">
            {CREDENTIAL_CLASSES.map((credentialClass) => (
              <FilterPill
                key={credentialClass}
                icon={ShieldIcon}
                label={t(`credential-classes.${credentialClass}`)}
                active={selectedCredentialClass === credentialClass}
                onToggle={() => toggleCredentialClass(credentialClass)}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1 text-sm text-text-tertiary">
          {t('filters.method')}
          <div className="flex flex-wrap gap-1.5">
            {METHODS.map((method) => (
              <FilterPill
                key={method}
                icon={TextIcon}
                label={method}
                active={selectedMethod === method}
                onToggle={() => toggleMethod(method)}
              />
            ))}
          </div>
        </div>
        <label className="flex flex-col gap-1 text-sm text-text-tertiary">
          {t('filters.workspace-id')}
          <CommitOnBlurInput
            className="h-8 w-72"
            value={readUuidParam(searchParams, 'workspaceId') ?? ''}
            placeholder={t('filters.workspace-placeholder')}
            aria-label={t('filters.workspace-id')}
            onCommit={(next) => commitUuid('workspaceId', next)}
          />
        </label>
        {/* Narrowing to ONE key is the other half of AC 62: the API-Key-Anfragen
            page carries a key filter but pins `credentialClass: 'api-key'`, so
            customer MCP traffic can only be pinned to a key here (IMM-601.6). */}
        <label className="flex flex-col gap-1 text-sm text-text-tertiary">
          {t('filters.api-key-id')}
          <CommitOnBlurInput
            className="h-8 w-72"
            value={readUuidParam(searchParams, 'apiKeyId') ?? ''}
            placeholder={t('filters.api-key-placeholder')}
            aria-label={t('filters.api-key-id')}
            onCommit={(next) => commitUuid('apiKeyId', next)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-text-tertiary">
          {t('filters.from')}
          <CommitOnBlurInput
            type="datetime-local"
            className="h-8"
            aria-label={t('filters.from')}
            value={isoToLocalInput(readDateParam(searchParams, 'from'))}
            onCommit={(next) => setParams({ from: toIso(next) })}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-text-tertiary">
          {t('filters.to')}
          <CommitOnBlurInput
            type="datetime-local"
            className="h-8"
            aria-label={t('filters.to')}
            value={isoToLocalInput(readDateParam(searchParams, 'to'))}
            onCommit={(next) => setParams({ to: toIso(next) })}
          />
        </label>
      </div>
      {rows.length === 0 ? (
        <ListEmptyState title={t('empty')} />
      ) : (
        <>
          <Table flush>
            <TableHeader>
              <TableRow {...table.columns.headerRowProps}>
                {table.columns.visibleColumns.map((column) => (
                  <OperatorColumnHead key={column.id} column={column} table={table} />
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {table.sorted.map((row) => (
                <TableRow key={row.id} interactive onClick={() => navigate(`/logs/api-calls/${row.id}`)}>
                  {table.columns.visibleColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                      style={column.cellStyle}
                    >
                      {column.cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {cursor !== null && (
            <div className="flex justify-center p-4">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex h-8 items-center gap-1 rounded-sm border border-border bg-surface px-3 text-base-sm font-medium text-text-secondary transition-colors duration-micro ease-out hover:bg-hover-bg focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-60"
              >
                {loadingMore ? t('load-more-loading') : t('load-more')}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
